import { callFirebaseFunction, storage } from 'boot/firebase';
import { i18n } from 'boot/i18n';
import IdxDb, { clearIndexedDbTable, clearOfflineModeData, indexedDbSchema } from 'boot/indexedDb';
import { setGlobalOfflineModeAvailable } from 'boot/network';
import { documentId } from 'firebase/firestore';
import { getBlob, ref } from 'firebase/storage';
import { chain, includes, isEmpty, omit, without } from 'lodash';
import { Loading } from 'quasar';
import { useTime } from 'src/composables/time';
import { ENCRYPTION_OBJECT_STORE } from 'src/constants/encryption';
import { toAccountIdentityDto } from 'src/contexts/identityAndTenancy';
import PlacesOfServiceDB from 'src/fb/placesOfService-db';
import SessionsDB from 'src/fb/sessions-db';
import TreatmentServicesDB from 'src/fb/treatmentServices-db';
import UsersDB from 'src/fb/users-db';
import UsersClientsDB from 'src/fb/usersClients-db';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import { auth as useAuthStore } from 'src/store/auth';
import { network as useNetworkStore } from 'src/store/network';
import { promptLevels as usePromptLevelsStore } from 'src/store/promptLevels';
import AppUtils, { userRoles } from 'src/utils/AppUtils';
import { readSecret } from 'src/utils/CryptoUtils';
import zlib from 'zlib';

/** @typedef {{match: string, newUrl: string}} ImageReplaceData */

const SYNC_DATE_ITEM_NAME = 'lastSyncData';
export const LOCAL_FILES_COLLECTION = 'filesStorage';
export const FILES_TO_UPLOAD_COLLECTION = 'filesUploading';

export const LOCAL_FILES_ATTR = 'data-storage-image';
export const FILES_TO_UPLOAD_ATTR = 'data-upload-image';

export const clearLocalData = async () => {
  Loading.show({ message: i18n.t('modals.offlineModal.clearOfflineData') });

  try {
    localStorage.removeItem(SYNC_DATE_ITEM_NAME);
    await clearOfflineModeData();
    await clearIndexedDbTable(ENCRYPTION_OBJECT_STORE);

    const network = useNetworkStore();
    await network.offlineModeSetStatus(false);
  } finally {
    Loading.hide();
  }
};

const convertBlobToBase64 = async (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

/**
 * Finds all images links in {@link str} and returns
 * @param {string} str - string we parse for storage images links
 * @returns {Record<[fullSrc: string], {fullSrc: string, imagePath: string}>} - array of paths
 */
function findStorageImagesPathsAndSources(str) {
  // eslint-disable-next-line no-useless-escape
  const imageSrcPattern = /<img.+?src="(https[\w\d:\/.-]+\/o\/([\w\d:\/.\-%]+).+?)">/gi;
  const matches = {};

  // Find all external images path
  let match = imageSrcPattern.exec(str);
  while (match) {
    if (match && match[0] && includes(match[0], 'https://firebasestorage.googleapis.com/') && match[1] && match[2]) {
      const fullSrc = match[1];
      const imagePath = decodeURIComponent(match[2]);

      matches[fullSrc] = { fullSrc, imagePath };
    }

    match = imageSrcPattern.exec(str);
  }

  return matches;
}

/**
 * @param {Object[]} sessionNotesTemplates
 * @param {GenericIndexedDb} idxDb
 * @return {Promise<void>}
 */
const saveSessionNoteTemplatesImages = async (sessionNotesTemplates, idxDb) => {
  const actions = sessionNotesTemplates.map((sessionNoteTemplate) => {
    const { templateText } = sessionNoteTemplate;

    // parse template text looking for images links
    const groupedImagesPaths = findStorageImagesPathsAndSources(templateText);

    const convertingActions = Object.keys(groupedImagesPaths).map(async (imgSrc) => {
      const data = groupedImagesPaths[imgSrc];
      if (!data) {
        return;
      }

      const { fullSrc, imagePath } = data;

      const fileRef = ref(storage, imagePath);
      const blobImage = await getBlob(fileRef);
      const base64 = await convertBlobToBase64(blobImage);
      await idxDb.set({
        base64,
        originalPath: imagePath,
        originalSrc: fullSrc,
      }, imagePath, true);
    });

    return AppUtils.allSettled(convertingActions);
  });

  await AppUtils.allSettled(actions);
};

const saveSyncDate = (userId) => {
  const data = { lastSyncTimestamp: new Date().getTime().toString(), userId };
  localStorage.setItem(SYNC_DATE_ITEM_NAME, JSON.stringify(data));
};

/**
 * Loops through the data, consistently saving every document into its collection.
 * Note: "Consistently" - is the most efficient way to save data in terms of time & space taken
 * ratio considering reliability of the algorithm.
 *
 * @todo Should be tested on the huge amount of data. Maybe rewrite using bulk methods.
 *
 * @param {{[p: collectionPath]: (Object[] | {[p: collectionPath]: Object[]})}} data
 * @return {Promise<void>}
 */
const saveDataToIndexedDb = async (data) => {
  const idxDb = new GenericIndexedDb();

  /* eslint-disable no-restricted-syntax, no-await-in-loop */
  for (const collectionName of Object.keys(data)) {
    idxDb.setCollectionPath(collectionName);
    const docs = data[collectionName];

    if (Array.isArray(docs)) {
      for (const doc of docs) {
        await idxDb.set(doc, doc.id, true);
      }
    }

    // save session note templates images into separate table
    if (collectionName === 'sessionNoteTemplates') {
      idxDb.setCollectionPath(LOCAL_FILES_COLLECTION);
      await saveSessionNoteTemplatesImages(docs, idxDb);
    }

    if (collectionName === 'subCollections') {
      for (const collectionPath of Object.keys(docs)) {
        const subDocs = docs[collectionPath];
        idxDb.setCollectionPath(collectionPath);

        for (const subDoc of subDocs) {
          await idxDb.set(subDoc, subDoc.id, true);
        }
      }
    }
  }
  /* eslint-enable no-restricted-syntax, no-await-in-loop */
};

/**
 * @param {Object<any>} deletedData
 */
const applyDeletedOfflineClients = async (deletedData) => {
  const auth = useAuthStore();

  const clients = deletedData.filter((data) => {
    return data.collectionName === 'clients' && auth.userSettings?.offlineClients.includes(data.id);
  });

  if (clients.length) {
    const deletedClients = clients.map((client) => client.id);
    const newOfflineClients = auth.userSettings.offlineClients.filter((id) => !deletedClients.includes(id));
    await auth.updateUserSettings({ offlineClients: newOfflineClients });

    return true;
  }

  return false;
};

/**
 * @param deletedData Object<any>
 * @returns {Promise<void>}
 */
const applyDiffDataToIndexedDb = async (deletedData) => {
  const idxDb = new GenericIndexedDb();

  // eslint-disable-next-line no-restricted-syntax
  for await (const data of deletedData) {
    const inMainCollection = indexedDbSchema[data.collectionName] ? data.collectionName : null;
    const inSubCollection = indexedDbSchema[`sub_${data.collectionName}`] ? `sub_${data.collectionName}` : null;
    const inSubSubCollection = indexedDbSchema[`sub_sub_${data.collectionName}`]
      ? `sub_sub_${data.collectionName}`
      : null;

    const collectionPath = inMainCollection || inSubCollection || inSubSubCollection;

    if (collectionPath) {
      idxDb.setCollectionPath(collectionPath);
      await idxDb.delete(data.id, true);
    }
  }
};

/**
 * @private
 * @return {Date|null}
 * */
const getPreviousSyncDate = (updateTimestamp = null) => {
  const lastSyncData = JSON.parse(localStorage.getItem(SYNC_DATE_ITEM_NAME));

  if (!lastSyncData?.lastSyncTimestamp) {
    return null;
  }

  const parsedSeconds = parseInt(lastSyncData.lastSyncTimestamp, 10);
  const previousDate = new Date(parsedSeconds);

  if (previousDate.toString() === 'Invalid Date') {
    return null;
  }

  if (updateTimestamp && updateTimestamp.toDate() > previousDate) {
    return null;
  }

  return previousDate;
};

/**
 * @return {Object}
 * */
const getFrontendDataForOffline = async () => {
  const auth = useAuthStore();
  const promptLevels = usePromptLevelsStore();

  const promptLevelTemplatesData = [];
  const promptLevelsData = {};

  Object.keys(promptLevels.promptLevelTemplates).forEach((templateId) => {
    const template = promptLevels.promptLevelTemplates[templateId];
    promptLevelTemplatesData.push(omit(template, 'promptLevels'));

    if (template.promptLevels && Array.isArray(template.promptLevels) && template.promptLevels.length) {
      promptLevelsData[`promptLevelTemplates/${templateId}/promptLevels`] = template.promptLevels;
    }
  });

  // Cache the clinic's treatment services for offline use. They live in their own root
  // collection, scoped by a `clinic` field, and the backend sync does not include them —
  // so they must be added to the offline payload explicitly. Read fresh (the store copy
  // is lazily loaded and may be empty at sync time).
  const clinicId = auth.userDetails?.clinic;
  let treatmentServices = [];
  if (clinicId) {
    try {
      const treatmentServicesDb = new TreatmentServicesDB();
      treatmentServices = await treatmentServicesDb.readAll([['clinic', '==', clinicId]]);
    } catch (err) {
      // A transient read failure here must not abort the whole offline payload —
      // fall back to an empty list so the rest of the offline data still assembles.
      console.error('Couldn\'t read treatment services for offline sync', err);
    }
  }

  // Cache the clinic's places of service for offline use. They live in their own root
  // collection, scoped by a `clinic` field, and the backend sync does not include them —
  // so they must be added to the offline payload explicitly. Read fresh (the store copy
  // is lazily loaded and may be empty at sync time).
  let placesOfService = [];
  if (clinicId) {
    try {
      const placesOfServiceDb = new PlacesOfServiceDB();
      placesOfService = await placesOfServiceDb.readAll([['clinic', '==', clinicId]]);
    } catch (err) {
      // A transient read failure here must not abort the whole offline payload —
      // fall back to an empty list so the rest of the offline data still assembles.
      console.error('Couldn\'t read places of service for offline sync', err);
    }
  }

  return {
    usersClients: auth.associatedClients,
    clinics: [auth.connectedClinic],
    promptLevelTemplates: promptLevelTemplatesData,
    treatmentServices: treatmentServices || [],
    placesOfService: placesOfService || [],
    subCollections: {
      ...promptLevelsData,
    },
  };
};

const unzipChart = (compressedData) => {
  if (!compressedData) {
    return;
  }
  return new Promise((resolve) => {
    zlib.gunzip(Buffer.from(compressedData, 'base64'), (error, result) => {
      resolve(JSON.parse(result.toString()));
    });
  });
};

/**
 * @param {Date} [previousSyncDate]
 * @return {Promise<Object>}
 * */
const getDataForOffline = async (previousSyncDate = null) => {
  const idxDb = new GenericIndexedDb('sessions');
  const runningSessions = await idxDb.readAll([['status', '==', 'running']]);
  const runningSessionsIds = runningSessions.map((session) => session.id);

  const { data: { data, compressed } } = await callFirebaseFunction('getDataForSync', {
    previousSyncDate: previousSyncDate?.getTime() || null,
    runningSessionsIds,
  });

  let resultData = data;

  if (compressed) {
    resultData = await unzipChart(data);
  }

  const availableData = await getFrontendDataForOffline();
  const combinedSubcollections = {
    ...availableData.subCollections,
    ...resultData.subCollections,
  };

  return {
    ...availableData,
    ...resultData,
    subCollections: combinedSubcollections,
  };
};

const getDeletedDataForOffline = async (previousSyncDate) => {
  const previousSyncTime = previousSyncDate?.getTime() || null;

  if (!previousSyncTime) {
    return;
  }

  const { data: { data, compressed } } = await callFirebaseFunction('getDeletedDataForSync', {
    previousSyncDate: previousSyncTime,
  });

  let resultData = data;

  if (compressed) {
    resultData = await unzipChart(data);
  }

  return resultData;
};

/**
 * @return {Promise<void>}
 * */
const rewriteOfflineStorage = async () => {
  Loading.show({ message: i18n.t('modals.offlineModal.downloadingOfflineData') });

  try {
    await clearOfflineModeData();
    localStorage.removeItem(SYNC_DATE_ITEM_NAME);

    const auth = useAuthStore();

    if (!auth.userSettings.offlineClients.length) {
      return;
    }

    const data = await getDataForOffline();

    await saveDataToIndexedDb(data);
    saveSyncDate(auth.userDetails.userId);
  } finally {
    Loading.hide();
  }
};

/**
 * @param {Date} previousSyncDate
 * @return {Promise<void>}
 * */
const updateOfflineDatabase = async (previousSyncDate) => {
  const auth = useAuthStore();
  if (!auth.userSettings.offlineClients.length) {
    await clearOfflineModeData();
    localStorage.removeItem(SYNC_DATE_ITEM_NAME);

    return;
  }

  const deletedData = await getDeletedDataForOffline(previousSyncDate);
  const shouldRewriteOfflineStorage = await applyDeletedOfflineClients(deletedData);

  if (shouldRewriteOfflineStorage) {
    await rewriteOfflineStorage();
    return;
  }

  await applyDiffDataToIndexedDb(deletedData);

  const data = await getDataForOffline(previousSyncDate);
  await saveDataToIndexedDb(data);

  saveSyncDate(auth.userDetails.userId);
};

/**
 * @param {string} privilege
 * @return {boolean}
 * */

function userHasPrivilege(privilege) {
  const auth = useAuthStore();

  const userRole = auth.userDetails?.role;

  const isLockedPrivilege = auth.lockedPrivileges?.[userRole]?.[privilege];
  const defaultPrivilege = auth.defaultPrivileges?.[userRole]?.[privilege] ?? false;

  if (isLockedPrivilege) {
    return defaultPrivilege;
  }

  return auth.userPrivileges[privilege] ?? defaultPrivilege ?? false;
}

/**
 * @param {Object} syncSettings
 * @param {boolean} syncSettings.resetDb
 * @param {boolean} [syncSettings.ignoreSyncDebounce]
 * @return {Promise<void>}
 * */
export async function syncToOffline({ resetDb, ignoreSyncDebounce } = {}) {
  const auth = useAuthStore();
  const { getDateDiff } = useTime();

  const hasOfflineClients = auth.userSettings.offlineClients && auth.userSettings.offlineClients.length > 0;
  const isOfflineSupported = userHasPrivilege('offline/hasAccess') && hasOfflineClients;

  setGlobalOfflineModeAvailable(isOfflineSupported);

  if (!isOfflineSupported) {
    try {
      localStorage.removeItem(SYNC_DATE_ITEM_NAME);
      await clearOfflineModeData();
    } catch (err) {
      console.error('Couldn\'t clear IndexedDB', err);
    } finally {
      // return from the function anyway
      // eslint-disable-next-line no-unsafe-finally
      return;
    }
  }

  const previousSyncDate = getPreviousSyncDate(auth.userSettings.updateTimestamp);

  if (resetDb || !previousSyncDate) {
    await rewriteOfflineStorage();
    return;
  }

  const now = new Date();

  // do not sync if the last sync was less than a minute ago; protection from often page reloads and new tabs
  if ((getDateDiff(now, previousSyncDate, 'seconds') <= 60) && !ignoreSyncDebounce) {
    return;
  }

  await updateOfflineDatabase(previousSyncDate);
}

/**
 * Stands in for the `getClientUsers` Cloud Function, and so answers in its shape: `identity` is a
 * plain object, because that is what a callable's JSON response delivers.
 *
 * @param {Object} data
 * @param {string} data.clientId
 * @param {string} data.sessionId
 * @param {string} data.clinicId
 * @param {Array<any>} data.skipRoles
 * @param {boolean} data.includeSelf
 * @param {string} userId
 * @return {Promise<Object>}
 * */

export async function getClientUsers(
  { clientId, sessionId, clinicId, skipRoles = [], includeSelf = false, userId, includeInactive },
) {
  let allowedRoles = [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR];
  let userClinic = clinicId;

  const sessionDb = new SessionsDB();
  let sessionDoc = {};

  if (sessionId) {
    sessionDoc = await sessionDb.read(sessionId);

    if (!sessionDoc) {
      throw new Error('Session doesn\'t exist');
    }

    const { limitedVisibility, limitHistoryVisibility, visibility, clinic, status } = sessionDoc;

    userClinic = clinic;

    if (((status === 'done') && limitHistoryVisibility)
      || ((status !== 'done') && limitedVisibility)) {
      if (!isEmpty(visibility)) {
        allowedRoles.push(...visibility);
      }
    } else {
      allowedRoles = [
        userRoles.MANAGER,
        userRoles.ADMINISTRATOR,
        userRoles.SUPERVISOR,
        userRoles.SENIOR_THERAPIST,
        userRoles.TRAINER,
        userRoles.PARENT,
      ];
    }
  } else {
    allowedRoles = [
      userRoles.MANAGER,
      userRoles.ADMINISTRATOR,
      userRoles.SUPERVISOR,
      userRoles.SENIOR_THERAPIST,
      userRoles.TRAINER,
      userRoles.PARENT,
    ];
  }

  const usersDb = new UsersDB();
  const usersClientsDb = new UsersClientsDB();

  const managersAndAdministratorsConstraints = [
    ['clinic', '==', userClinic],
    ['role', 'in', [userRoles.MANAGER, userRoles.ADMINISTRATOR]],
  ];
  const defaultConstraints = [['client', '==', clientId], ['role', 'in', allowedRoles]];

  if (!includeSelf) {
    managersAndAdministratorsConstraints.push([documentId(), '!=', userId]);
    defaultConstraints.push(['user', '!=', userId]);
  }

  const managersAndAdministratorsData = usersDb.readAll(managersAndAdministratorsConstraints);
  const defaultData = usersClientsDb.readAll(defaultConstraints);

  const [clientUsers, managersAndAdministrators] = await Promise.all([
    defaultData,
    managersAndAdministratorsData,
  ]);

  const managersAndAdministratorsDocs = managersAndAdministrators.reduce((acc, account) => {
    const { id, name, status, role, isSupporting } = account;

    if ((status !== 'inactive' || includeInactive) && !isSupporting) {
      acc.push({
        user: id,
        userName: name,
        role,
        identity: toAccountIdentityDto({ ...account, userId: id }),
      });
    }

    return acc;
  }, []);

  let clientUsersDocs = clientUsers.reduce((acc, { userName, user, role, isSupporting }) => {
    if (user && !skipRoles?.includes(role) && !isSupporting) {
      acc.push({ user, userName, role });
    }

    return acc;
  }, []);

  // The accounts behind the client's own users, which came from `usersClients` and carry no link to
  // a person. Read unfiltered rather than narrowed to the inactive ones, because the same documents
  // answer both questions and IndexedDB does not bill per document the way Firestore does.
  const clientUserAccounts = clientUsersDocs.length
    ? await usersDb.readAll([
      [documentId(), 'in', clientUsersDocs.map((clientUserDoc) => clientUserDoc.user)],
    ])
    : [];
  const accountsById = new Map(clientUserAccounts.map((account) => [account.id, account]));

  if (clientUsersDocs.length && !includeInactive) {
    clientUsersDocs = clientUsersDocs.filter(
      ({ user }) => accountsById.get(user)?.status !== 'inactive',
    );
  }

  clientUsersDocs = clientUsersDocs.map((clientUserDoc) => {
    const account = accountsById.get(clientUserDoc.user);

    return {
      ...clientUserDoc,
      identity: account ? toAccountIdentityDto({ ...account, userId: account.id }) : null,
    };
  });

  const result = chain(managersAndAdministratorsDocs)
    .concat(clientUsersDocs)
    .uniqBy('user')
    .sortBy('userName')
    .value();

  return { data: result };
}

export async function clearRedundantOfflineData() {
  // @todo: handle other sub-collections
  const idxDb = new GenericIndexedDb();

  idxDb.setCollectionPath('activities');
  await idxDb.clearTable();

  idxDb.setCollectionPath('sessions');
  const offlineDoneSessions = await idxDb.readAll([['status', 'not-in', ['scheduled', 'running']]]);
  const sessionIds = offlineDoneSessions.map((session) => session.id);
  await Promise.all(sessionIds.map((sessionId) => idxDb.delete(sessionId, true)));

  idxDb.setCollectionPath('sessionActivities');
  const sessionActivities = await idxDb.readAll([['session', 'in', sessionIds]]);
  const staffOnlyActivities = await idxDb.readAll([['status', 'in', ['finished', 'closed']], ['client', '==', null]]);
  await Promise.all(sessionActivities.concat(staffOnlyActivities).map((sessionActivity) => {
    return idxDb.delete(sessionActivity.id, true);
  }));

  idxDb.setCollectionPath('data');
  const data = await idxDb.readAll([['session', 'in', sessionIds]]);
  await Promise.all(data.map((dataItem) => idxDb.delete(dataItem.id, true)));

  idxDb.setCollectionPath('authSessions');
  await idxDb.clearTable();
}

/**
 * @param {string} html
 * @return {{attrs: string, referenceId: string}[]}
 */
export function findImagesToUploadReferences(html) {
  const imageSrcPattern = /<img.+?(src=""[^>]*?data-upload-image="(.+?)").*?>/gi;
  const matches = new Set();

  let match = imageSrcPattern.exec(html);
  while (match) {
    if (match && match[0] && match[1]) {
      // eslint-disable-next-line no-unused-vars
      const [_, srcAndDataAttrs, referenceId] = match;
      matches.add({ attrs: srcAndDataAttrs, referenceId });
    }

    match = imageSrcPattern.exec(html);
  }

  return Array.from(matches);
}

/**
 * Create string to replace image src value and set data-attribute containing IDB document id
 * to `filesStorage` or `filesUploading` document.
 *
 * @param {string} docId
 * @param {string} [data] value to replace src
 * @param {'fromStorage' | 'toUpload'} [type]
 * fromStorage - images stored in IDB `filesStorage` table.
 * toUpload - images stored in IDB `filesUploading` table.
 */
export function createSrcReplacer(docId, data, type = 'toUpload') {
  let replacer = `${data || ''}"`;

  if (type === 'fromStorage') {
    replacer += ` ${LOCAL_FILES_ATTR}`;
  } else {
    replacer += ` ${FILES_TO_UPLOAD_ATTR}`;
  }

  return `${replacer}="${docId}`;
}

/**
 * @param {string} html
 * @return {Promise<ImageReplaceData[]>}
 */
async function findImagesToUploadReplaceData(html) {
  const filesUploadingReferences = findImagesToUploadReferences(html);

  const filesUploadingDb = new GenericIndexedDb(FILES_TO_UPLOAD_COLLECTION);
  const filesUploadingDataActions = filesUploadingReferences.map(async (referenceData) => {
    const { attrs, referenceId } = referenceData;

    const fileData = await filesUploadingDb.read(referenceId);
    const attrsWithUpdatedSrc = `src="${createSrcReplacer(referenceId, fileData.file)}"`;

    return {
      match: attrs,
      newUrl: attrsWithUpdatedSrc,
    };
  });

  const filesUploadingDataResults = await AppUtils.allSettled(filesUploadingDataActions);
  return filesUploadingDataResults.map((data) => data.value).filter((data) => !!(data));
}

/**
 * @param {string} html
 * @return {Promise<ImageReplaceData[]>}
 */
async function findStorageImagesReplaceData(html) {
  const groupedStorageImages = findStorageImagesPathsAndSources(html);
  const filesStorageDb = new GenericIndexedDb(LOCAL_FILES_COLLECTION);

  const gettingLocalImageActions = Object.keys(groupedStorageImages).map(async (imgSrc) => {
    const data = groupedStorageImages[imgSrc];
    if (!data) {
      return;
    }

    const { fullSrc, imagePath } = data;
    const base64ImageDocument = await filesStorageDb.read(imagePath);
    if (!base64ImageDocument) {
      return;
    }

    const { base64 } = base64ImageDocument;
    return {
      match: fullSrc,
      newUrl: createSrcReplacer(imagePath, base64, 'fromStorage'),
    };
  });

  const localImagesDataResult = await AppUtils.allSettled(gettingLocalImageActions);
  return localImagesDataResult.map((result) => result.value).filter((item) => !!(item));
}

/**
 * @param {string} html
 * @return {Promise<string>}
 */
export async function replaceStorageImagesWithLocal(html) {
  const [storageImagesReplaceData, imagesToUploadReplaceData] = await Promise.all([
    findStorageImagesReplaceData(html),
    findImagesToUploadReplaceData(html),
  ]);

  const replaceUrlData = storageImagesReplaceData.concat(...imagesToUploadReplaceData);

  // Update string with new image sources
  let newStr = html;
  replaceUrlData.forEach((data) => {
    newStr = newStr.replaceAll(data.match, data.newUrl);
  });

  return newStr;
}

/**
 * @param {string} html
 * @return {Element[]}
 */
export function findBase64ImageDOMElements(html) {
  const parser = new DOMParser();
  const document = parser.parseFromString(html, 'text/html');
  const { body } = document;

  const base64ImagesElements = body.querySelectorAll('img[src*="data:image"]');

  return Array.from(base64ImagesElements);
}

/**
 * Find all base64 images within the html string and replace each with its Firebase Storage link if image exists
 * in IDB or clear src of the images that are about to be uploaded.
 *
 * Images from IDB storage should look like this after replacing - `<img src="[link to store]">`
 * Images to upload should look like this after replacing - `<img src="" data-upload-image="[doc id]">`
 *
 * @param {string} html
 * @return {Promise<string>}
 */
export async function replaceLocalImagesWithStorage(html) {
  const base64Images = findBase64ImageDOMElements(html);
  if (!base64Images.length) {
    return html;
  }

  const filesStorageDb = new GenericIndexedDb(LOCAL_FILES_COLLECTION);

  const getDataToReplaceActions = base64Images.map(async (base64ImageElement) => {
    if (base64ImageElement.hasAttribute(LOCAL_FILES_ATTR)) {
      // image was taken from the `filesStorage` IDB collection
      const base64 = base64ImageElement.getAttribute('src');
      const imageFileId = base64ImageElement.getAttribute(LOCAL_FILES_ATTR);

      const imageFile = await filesStorageDb.read(imageFileId);
      if (!imageFile) {
        return;
      }

      const valueToReplace = createSrcReplacer(imageFileId, base64, 'fromStorage');

      return {
        match: valueToReplace,
        newValue: imageFile.originalSrc,
      };
    }

    if (base64ImageElement.hasAttribute(FILES_TO_UPLOAD_ATTR)) {
      // image was taken from the `filesUploading` IDB collection
      const base64 = base64ImageElement.getAttribute('src');
      const replacer = '';

      return {
        match: base64,
        newValue: replacer,
      };
    }
  });

  let dataToReplace = await AppUtils.allSettled(getDataToReplaceActions);
  dataToReplace = dataToReplace.map((result) => result.value).filter((data) => !!(data));

  let newStr = html;
  dataToReplace.forEach((data) => {
    newStr = newStr.replaceAll(data.match, data.newValue);
  });

  return newStr;
}

/**
 * @param {string} oldHtml initial html with images to upload being stored as references
 *  within `data-upload-image-src` attr.
 * @param {string} newHtml - updated html with embedded base64 images
 * @return {Promise<string[]>}
 */
export async function findImagesToDeleteFromUploadingQueue(oldHtml, newHtml) {
  const initImagesToUploadRefs = findImagesToUploadReferences(oldHtml);
  const changedImagesToUploadRefs = findImagesToUploadReferences(newHtml);

  const initImagesIds = initImagesToUploadRefs.map((reference) => reference.referenceId);
  const changedImagesIds = changedImagesToUploadRefs.map((reference) => reference.referenceId);

  return without(initImagesIds, ...changedImagesIds);
}

export async function deleteDecryptionKey() {
  const decryptionKey = await readSecret('decryptionKey');

  if (!decryptionKey) {
    return;
  }

  return IdxDb[ENCRYPTION_OBJECT_STORE].delete('decryptionKey');
}
